// =============================================================================
// components/learn/MustSeeRail.tsx — horizontal "Must-see" rail for Learn.
//
// Admin-curated featured articles (LearnArticle.featured) rendered as a
// horizontal deck of bigger gradient/cover cards with a flame "MUST-SEE"
// badge. Used in two places:
//   • LearnScreen — hero rail above the level pills.
//   • LearnArticleScreen — "keep reading" rail below the article body
//     (falls back to same-level recents; the rail hides itself when empty).
//
// The rail renders NOTHING when there are no articles — never an empty shell.
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import CachedImage from '../ui/CachedImage';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons } from '../ui/Icon';
import { colors, spacing, radius, fonts } from '../../constants/theme';
import { gradientFor, levelLabel } from './gradients';
import { learnCover } from '../../lib/icons';

export interface MustSeeArticle {
  id?: string;
  slug?: string;
  title?: string;
  description?: string;
  excerpt?: string;
  difficulty?: string | null;
  image?: string;
  imageUrl?: string;
}

interface RailProps {
  articles: MustSeeArticle[];
  onPressArticle: (slug: string) => void;
  /** Small uppercase kicker above the heading. */
  kicker?: string;
  /** Rail heading. */
  title?: string;
  /** Show the flame MUST-SEE badge on cards (off for the recents fallback). */
  showBadge?: boolean;
  /**
   * Horizontal padding of the parent container — the rail bleeds out of it so
   * cards scroll edge-to-edge while the first card still aligns to the gutter.
   */
  bleed?: number;
  style?: object;
}

function MustSeeCard({
  article, index, showBadge, onPress,
}: { article: MustSeeArticle; index: number; showBadge: boolean; onPress: () => void }) {
  // #44: always a real featured image (article's own, else a stable topic photo).
  const cover = learnCover(article.title, article.slug, article.image || article.imageUrl, 800);
  const gradient = gradientFor(index);

  const inner = (
    <>
      <View style={styles.cardTopRow}>
        {showBadge && (
          <View style={styles.badge}>
            <Ionicons name="flame" size={11} color="#FFD166" />
            <Text style={styles.badgeText}>Must-see</Text>
          </View>
        )}
      </View>
      <View style={styles.cardBottom}>
        <Text style={styles.cardTitle} numberOfLines={3}>
          {article.title || 'Untitled'}
        </Text>
        <View style={styles.cardFooter}>
          <View style={styles.levelChip}>
            <Text style={styles.levelChipText}>{levelLabel(article.difficulty)}</Text>
          </View>
          <View style={styles.arrowCircle}>
            <Ionicons name="arrow-forward" size={13} color="#FFFFFF" />
          </View>
        </View>
      </View>
    </>
  );

  return (
    <TouchableOpacity
      style={styles.card}
      activeOpacity={0.85}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`${showBadge ? 'Must-see article' : 'Article'}: ${article.title || 'lesson'}`}
    >
      {cover ? (
        <View style={styles.cardCover}>
          <CachedImage source={{ uri: cover }} style={StyleSheet.absoluteFill} resizeMode="cover" />
          <LinearGradient
            colors={['rgba(26,26,46,0.18)', 'rgba(26,26,46,0.78)']}
            style={[StyleSheet.absoluteFill, styles.cardInner]}
          >
            {inner}
          </LinearGradient>
        </View>
      ) : (
        <LinearGradient
          colors={[...gradient]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={[styles.cardCover, styles.cardInner]}
        >
          <Ionicons
            name="school"
            size={92}
            color="rgba(255,255,255,0.12)"
            style={styles.ghostIcon}
          />
          {inner}
        </LinearGradient>
      )}
    </TouchableOpacity>
  );
}

export default function MustSeeRail({
  articles,
  onPressArticle,
  kicker = 'MUST-SEE',
  title = 'Must-see articles',
  showBadge = true,
  bleed = 0,
  style,
}: RailProps) {
  const list = articles.filter((a) => !!a.slug);
  if (list.length === 0) return null; // never render an empty section

  return (
    <View style={style}>
      <Text style={styles.kicker}>{kicker}</Text>
      <Text style={styles.heading}>{title}</Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={bleed ? { marginHorizontal: -bleed } : undefined}
        contentContainerStyle={[styles.railContent, bleed ? { paddingHorizontal: bleed } : undefined]}
        decelerationRate="fast"
        snapToInterval={CARD_WIDTH + spacing.md}
        snapToAlignment="start"
      >
        {list.map((a, i) => (
          <MustSeeCard
            key={a.slug ?? a.id ?? String(i)}
            article={a}
            index={i}
            showBadge={showBadge}
            onPress={() => a.slug && onPressArticle(a.slug)}
          />
        ))}
      </ScrollView>
    </View>
  );
}

const CARD_WIDTH = 248;

const styles = StyleSheet.create({
  kicker: {
    ...fonts.bold, fontSize: 11, color: colors.primary,
    letterSpacing: 1.2, marginBottom: 2,
  },
  heading: {
    ...fonts.extrabold, fontSize: 20, color: colors.ink,
    marginBottom: spacing.md,
  },
  railContent: {
    gap: spacing.md,
  },
  card: {
    width: CARD_WIDTH,
    borderRadius: radius.xl,
    overflow: 'hidden',
    shadowColor: 'rgba(108,92,231,0.35)',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 1,
    shadowRadius: 12,
    elevation: 3,
  },
  cardCover: {
    minHeight: 156,
    borderRadius: radius.xl,
    overflow: 'hidden',
  },
  cardInner: {
    padding: spacing.md,
    justifyContent: 'space-between',
  },
  ghostIcon: {
    position: 'absolute',
    right: -14,
    top: -12,
  },
  cardTopRow: {
    flexDirection: 'row',
    minHeight: 22,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(26,26,46,0.35)',
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: 4,
  },
  badgeText: {
    ...fonts.bold, fontSize: 10, color: '#FFFFFF',
    letterSpacing: 0.6, textTransform: 'uppercase',
  },
  cardBottom: {
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  cardTitle: {
    ...fonts.extrabold, fontSize: 17, lineHeight: 22, color: '#FFFFFF',
  },
  cardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  levelChip: {
    backgroundColor: 'rgba(255,255,255,0.24)',
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: 3,
  },
  levelChipText: {
    ...fonts.bold, fontSize: 10, color: '#FFFFFF',
    letterSpacing: 0.5, textTransform: 'uppercase',
  },
  arrowCircle: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: 'rgba(255,255,255,0.24)',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
